import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IconType } from 'components/layout/icon/icon.component';
import { SiteType } from 'enums/SiteType';
import { environment } from 'environments/environment';
import { ServerModel } from 'models/generated/Distribution.Api.Models.Server';
import { RedesignStatus, SiteModel, SiteStatus } from 'models/generated/Distribution.Api.Models.Site';
import { SiteDtoModel } from 'models/generated/Distribution.Api.Models.SiteDto';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { firstValueFrom } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from 'services/push.service';

@Injectable()
export class SiteService {

	private baseUrl = `${environment.gatewayUrl}/api/site`;
	private serverUrl = `${environment.gatewayUrl}/api/server`;
	private gatewayUrl = `${environment.gatewayUrl}/api/gateway/site`;

	private testAddressRegex = /\.dana(\d+)\.|\.dws(\d+)\.|\.danaweb(\d+)\.|\.gocms(\d+)\.|\.danaweb\.com|\.danaweb\.org|\.grouponline\.org|\.webserver(\d+)\./i;
	private wwwRegex = /^www\./i;

	private siteTracker = new LiveDataTracker<SiteModel, { siteId: string, productPlanId?: string }>(site => site.id!);

	private serversPromise?: Promise<ServerModel[]>;

	constructor(private readonly http: HttpClient, private readonly translate: TranslatePipe, private readonly customDate: CustomDatePipe, private readonly pushService: PushService) {
		this.pushService.onSiteChanges.subscribe(event => {
			this.siteTracker.refreshData(event, event.data.siteId, () => this.getSite(event.data.siteId));
		});
	}

	getSite(siteId: string, trackingContext?: TrackingContext): Promise<SiteModel> {
		return firstValueFrom(this.http.get<SiteModel>(`${this.baseUrl}/${siteId}`))
			.then(site => this.siteTracker.trackObjectForComponent(trackingContext, site));
	}

	getSitesByProductPlanIds(productPlanIds: string[], trackingContext?: TrackingContext): Promise<SiteModel[]> {
		return firstValueFrom(this.http.post<SiteModel[]>(`${this.baseUrl}/by-product-plan-ids`, productPlanIds))
			.then(sites => this.siteTracker.trackArrayForComponent(trackingContext, sites, eventData => !!eventData.productPlanId && productPlanIds.indexOf(eventData.productPlanId) >= 0));
	}

	getSiteName(site: SiteModel): string {
		return site.pointers && site.pointers.length > 0
			? site.pointers.sort((a, b) => {
				return ((a.primaryPointer ? '0' : '1') + a.name).localeCompare((b.primaryPointer ? '0' : '1') + b.name);
			})[0].name
			: '';
	}

	getSiteStatus(site: SiteModel): string {
		let status: string;
		switch (site.status) {
			case SiteStatus.ActivatingRedesign:
				status = `${this.translate.transform('site.site-status.' + SiteStatus[site.status])}${(site.redesignStatus != RedesignStatus.None ? ' - ' + this.translate.transform('site.site-redesign-status.' + RedesignStatus[site.redesignStatus]) : '')}`;
				break;
			case SiteStatus.Working:
				status = this.translate.transform('site.site-status.' + SiteStatus[site.status]);
				break;
			case SiteStatus.Failed:
				status = this.translate.transform('site.site-status.' + SiteStatus[site.status], { message: site.message });
				break;
			case SiteStatus.Completed:
				status = this.translate.transform('site.site-status.' + SiteStatus[site.status], { created: this.customDate.transform(site.created, 'shortDateText') });
				break;
			default:
				status = this.translate.transform('site.site-status.' + SiteStatus[site.status]);
				break;
		}
		return status;
	}

	isTestAddress(pointer: string): boolean {
		return this.testAddressRegex.test(pointer);
	}

	getSitePrimaryPointer(site: SiteModel): string | undefined {
		return site.pointers.find(pointer => pointer.primaryPointer)?.name;
	}

	getServers(): Promise<ServerModel[]> {
		if (this.serversPromise) return this.serversPromise;
		return this.serversPromise = firstValueFrom(this.http.get<ServerModel[]>(`${this.serverUrl}`)).then(servers => {
			return servers;
		});
	}

	setSitePrimaryPointer(siteId: string, pointerId: string) {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${siteId}/set-site-primary-pointer/${pointerId}`, siteId));
	}

	changeProductPlan(siteId: string, productPlanId: string) {
		return firstValueFrom(this.http.get<void>(`${this.baseUrl}/${siteId}/change-product-plan-id/${productPlanId}`));
	}

	createRedesign(siteId: string) {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${siteId}/redesign`, null));
	}

	activateRedesign(siteId: string) {
		return firstValueFrom(this.http.get<void>(`${this.baseUrl}/${siteId}/activate`));
	}

	deleteSite(siteId: string) {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/delete/${siteId}`));
	}

	autoDeleteSite(siteId: string) {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/auto-delete/${siteId}`));
	}

	cancelAutoDelete(siteId: string) {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/cancel-auto-delete/${siteId}`, siteId));
	}

	deletePointer(pointerId: string): Promise<string> {
		return firstValueFrom(this.http.delete<string>(`${this.baseUrl}/pointers/${pointerId}`));
	}

	createSite(type: SiteType, pointer: string, companyId: string, productPlanId: string): Promise<SiteModel> {
		const site = new SiteDtoModel();
		site.type = type;
		site.pointerName = pointer;
		site.companyId = companyId;
		site.productPlanId = productPlanId;
		return firstValueFrom(this.http.post<SiteModel>(`${this.gatewayUrl}`, site));
	}

	addPointerToSite(pointer: string, site: SiteModel): Promise<string> {
		return firstValueFrom(this.http.post<string>(`${this.baseUrl}/${site.id}/pointers?pointerName=${pointer.toLowerCase()}`, null));
	}

	getSiteIcon(siteType: SiteType): IconType {
		switch (siteType) {
			case SiteType.Dsa:
				return 'logo';
			case SiteType.WordPressProvisioningApi:
			case SiteType.WebserverApi:
				return 'wordpress';
			case SiteType.AiBuilder:
				return 'user-robot';
			default:
				return 'globe';
		}
	}

	addSsl(siteId: string, validPointers: string[]) {
		return firstValueFrom(this.http.post<string>(`${this.baseUrl}/add-ssl/${siteId}`, validPointers));
	}
}
